import { severityMap } from 'components';
import { EventItem, EventItemExtended } from 'queries/events/types';
import { Classification, Status, Severity } from 'queries/assets/contract';
import { formatDurationString } from 'utils';

export const ASSIGNED_CHECKS_ENUM = {
  EMPTY: 0,
  PARTIAL: 1,
  FULL: 2,
} as const;

const getParentEventClassification = (events: EventItem[]): Classification => {
  if (events.every((event) => event.classification === Classification.USEFUL)) {
    return Classification.USEFUL;
  }
  if (events.every((event) => event.classification === Classification.NOT_USEFUL)) {
    return Classification.NOT_USEFUL;
  }
  return null as unknown as Classification;
};

const getParentEventStatus = (events: EventItem[]): Status => {
  if (events.every((event) => event.status === Status.CLOSED)) {
    return Status.CLOSED;
  }
  if (events.some((event) => event.status === Status.OPEN)) {
    return Status.OPEN;
  }
  return '-' as Status;
};

const getNewEventEndDate = (data: EventItem[]) => {
  if (data.some((d) => d.alarm_datetime_end === null || d.alarm_datetime_end === 'NaT')) {
    return null;
  }
  return data.reduce((a, b) => {
    return new Date(a.alarm_datetime_end) > new Date(b.alarm_datetime_end) ? a : b;
  }).alarm_datetime_end;
};

export const processEvents = (events: EventItemExtended[]) => {
  const firstEvent = events[0];

  const is_automatic = events.some(({ is_automatic }) => is_automatic);
  const is_automatic_blocked = events.some(({ is_automatic_blocked }) => is_automatic_blocked);
  const checkedCount = events?.filter(({ is_assigned_to_case }) => is_assigned_to_case).length;

  const assignedChecks =
    checkedCount === events.length
      ? ASSIGNED_CHECKS_ENUM.FULL
      : checkedCount === 0
      ? ASSIGNED_CHECKS_ENUM.EMPTY
      : ASSIGNED_CHECKS_ENUM.PARTIAL;

  return events.reduce<EventItemExtended>(
    (acc, current) => {
      const currentStart = new Date(current.alarm_datetime_start);
      const accStart = new Date(acc.alarm_datetime_start);

      const closed_by = current.closed_by ?? acc.closed_by ?? '';
      const prognosis = acc.prognosis || current.prognosis;

      return {
        ...acc,
        alarm_datetime_start:
          currentStart < accStart ? current.alarm_datetime_start : acc.alarm_datetime_start,
        alarm_datetime_end: getNewEventEndDate(events) ?? '',
        closed_by,
        duration: formatDurationString(acc.alarm_datetime_start, acc.alarm_datetime_end),
        is_automatic,
        is_automatic_blocked,
        assignedChecks,
        prognosis,
        severity:
          severityMap[current.severity] > severityMap[acc.severity]
            ? current.severity
            : acc.severity,
      };
    },
    {
      ...firstEvent,
      alarm_datetime_start: firstEvent.alarm_datetime_start,
      alarm_datetime_end: getNewEventEndDate(events) ?? '',
      severity: 'Information' as Severity,
    },
  );
};

interface GroupedEvents {
  [key: string]: EventItemExtended[];
}

const groupByAssetIdAndAlarmName = (data: EventItemExtended[]): GroupedEvents => {
  return data.reduce((acc: GroupedEvents, curr) => {
    const uniqueId = `${curr.asset?.id}|${curr.name}`;
    if (!acc[uniqueId]) {
      acc[uniqueId] = [];
    }
    acc[uniqueId].push(curr);
    return acc;
  }, {});
};

export const groupEvents = (data: EventItem[]): EventItemExtended[] => {
  const groupedData = groupByAssetIdAndAlarmName(data);

  return Object.values(groupedData).map((events) => {
    const isSingleItem = events.length === 1;
    const firstEvent = events[0];
    if (isSingleItem) return firstEvent;

    const processedEvents = processEvents(events);

    return {
      ...processedEvents,
      id: `${firstEvent.id}-CLONE`,
      children: events,
      classification: getParentEventClassification(events),
      status: getParentEventStatus(events),
    };
  });
};
